import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';
import clsx from 'clsx';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
  onClose: () => void;
  index: number;
}

export function Toast({ message, type, duration = 5000, onClose, index }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-success-500" />,
    error: <AlertCircle className="w-5 h-5 text-error-500" />,
    warning: <AlertCircle className="w-5 h-5 text-warning-500" />,
    info: <Info className="w-5 h-5 text-info-500" />
  };

  const backgrounds = {
    success: 'bg-success-50 dark:bg-[rgb(var(--success-900)/0.2)]',
    error: 'bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)]',
    warning: 'bg-warning-50 dark:bg-[rgb(var(--warning-900)/0.2)]',
    info: 'bg-info-50 dark:bg-[rgb(var(--info-900)/0.2)]'
  };

  const borders = {
    success: 'border-success-200',
    error: 'border-error-200',
    warning: 'border-warning-200',
    info: 'border-info-200'
  };

  return (
    <div
      className={clsx(
        'transform transition-all duration-300 ease-in-out mb-2',
        backgrounds[type],
        borders[type],
        'border rounded-lg shadow-lg',
        'flex items-center space-x-3 p-4 min-w-[320px] max-w-[420px]',
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100',
        'relative'
      )}
      style={{
        marginTop: index * 4 + 'px'
      }}
      role="alert"
    >
      {icons[type]}
      <p className="flex-1 text-sm font-medium text-[--text-primary]">
        {message}
      </p>
      <button
        onClick={() => setIsExiting(true)}
        className="text-[--text-disabled] hover:text-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--text-disabled] focus:ring-offset-2 focus:ring-offset-transparent rounded-full"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}